
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Edit, Trash2, Image } from 'lucide-react';
import ShareMemory from '@/components/memories/ShareMemory';
import QuoteCardDialog from '@/components/quotes/QuoteCardDialog';
import { QuoteItem, quoteToMemory, updateQuote, getStoredChildren, deleteQuote } from '@/utils/storage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { logger } from '@/utils/logger';

interface QuoteCardProps {
  quote: QuoteItem;
  onQuoteUpdated?: (updatedQuote: QuoteItem) => void;
  onQuoteDeleted?: (quoteId: string) => void;
}

const QuoteCard = ({ quote, onQuoteUpdated, onQuoteDeleted }: QuoteCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [editingQuote, setEditingQuote] = useState<QuoteItem>(quote);
  const [children, setChildren] = useState<{ id: string; name: string }[]>([]);

  // Load children data when editing starts
  useEffect(() => {
    if (isEditing) {
      const fetchChildren = async () => {
        try {
          const storedChildren = await getStoredChildren();
          if (storedChildren.length > 0) {
            setChildren(storedChildren.map(child => ({ id: child.id, name: child.name })));
          }
          // Reset the editing quote data
          setEditingQuote({ ...quote });
        } catch (error) {
          logger.error("Error fetching children:", error);
          toast({
            title: "Error",
            description: "Failed to load children data.",
            variant: "destructive"
          });
        }
      };
      
      fetchChildren();
    }
  }, [isEditing, quote]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleUpdateQuote = async () => {
    if (!editingQuote.content || !editingQuote.childId || !editingQuote.date) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    const updatedChildName = children.find(child => child.id === editingQuote.childId)?.name || editingQuote.childName;
    const updatedQuote = { ...editingQuote, childName: updatedChildName };

    try {
      // Update quote in localStorage or Supabase
      const result = await updateQuote(updatedQuote);

      // Close dialog and notify parent
      setIsEditing(false);

      if (onQuoteUpdated && result) {
        onQuoteUpdated(result);
      } else if (onQuoteUpdated) {
        // Fallback if result is null but we still want to update UI
        onQuoteUpdated(updatedQuote);
      }
    } catch (error) {
      logger.error("Error updating quote:", error);
      toast({
        title: "Error",
        description: "Failed to update quote. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteQuote = async () => {
    try {
      const success = await deleteQuote(quote.id);

      if (success) {
        setIsDeleting(false);

        if (onQuoteDeleted) {
          onQuoteDeleted(quote.id);
        }

        toast({
          title: "Quote deleted",
          description: "The quote has been deleted successfully!"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete quote. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      logger.error("Error deleting quote:", error);
      toast({
        title: "Error",
        description: "Failed to delete quote. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <Card key={quote.id}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span>{quote.childName}</span>
            <span className="text-gray-500 text-sm ml-auto font-normal">
              {new Date(quote.date).toLocaleDateString()}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="italic text-left">
            {quote.content}
          </p>
          {quote.context && (
            <p className="text-sm text-gray-500 mt-1 text-left">{quote.context}</p>
          )}
        </CardContent>
        <CardFooter className="flex justify-start space-x-2 pt-0">
          <Button variant="outline" size="sm" onClick={handleEditClick}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsDeleting(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <ShareMemory memory={quoteToMemory(quote)} />
          <Button variant="outline" size="sm" onClick={() => setIsCreatingCard(true)}>
            <Image className="h-4 w-4 mr-2" />
            Card
          </Button>
        </CardFooter>
      </Card>

      {/* Edit Quote Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-[500px] w-[95%]">
          <DialogHeader>
            <DialogTitle>Edit Quote</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-child">Child</Label>
              <Select
                onValueChange={(value) => setEditingQuote({...editingQuote, childId: value})}
                value={editingQuote.childId}
              >
                <SelectTrigger className="rounded-md">
                  <SelectValue placeholder="Select a child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map(child => (
                    <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-quote">Quote</Label>
              <Textarea
                id="edit-quote"
                placeholder="What did they say?"
                value={editingQuote.content}
                onChange={(e) => setEditingQuote({...editingQuote, content: e.target.value})}
                className="min-h-[80px] rounded-md max-h-[150px] overflow-y-auto"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={editingQuote.date}
                onChange={(e) => setEditingQuote({...editingQuote, date: e.target.value})}
                className="rounded-md"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-context">Context (optional)</Label>
              <Textarea
                id="edit-context"
                placeholder="What was happening when they said this?"
                value={editingQuote.context || ''}
                onChange={(e) => setEditingQuote({...editingQuote, context: e.target.value})}
                className="rounded-md max-h-[100px] overflow-y-auto"
              />
            </div>
            <Button onClick={handleUpdateQuote} className="mt-2 rounded-full">Update Quote</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote Card Image Dialog */}
      <QuoteCardDialog
        open={isCreatingCard}
        onOpenChange={setIsCreatingCard}
        data={{
          content: quote.content,
          childName: quote.childName,
          date: quote.date,
          context: quote.context,
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleting} onOpenChange={setIsDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this quote. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteQuote} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default QuoteCard;

